import { ActionDirectionType, EffectShape, LagType } from '../types/enums';
import type {
  TrinketBasicAttack,
  TrinketId,
  TrinketMajorDefinition,
  TrinketMinorDefinition,
} from '../types/trinket.types';
import type { SpellDefinition } from '../types/spell.types';

/**
 * Builds a synthetic `SpellDefinition` from a basic-attack template
 * carried by a major trinket. The synthetic ID lets cooldowns isolate per
 * trinket.
 */
export function buildSpellFromTemplate(
  template: TrinketBasicAttack,
  syntheticId: string,
): SpellDefinition {
  const impactPhase: 'impact' | 'action' =
    template.projectile || template.hitscan ? 'impact' : 'action';
  return {
    id: syntheticId,
    name: 'Attaque de base',
    parameter: { type: 'position' },
    directionType: template.directionType ?? ActionDirectionType.ALL,
    range: template.range,
    rangeAlgorithm: template.rangeAlgorithm,
    shapes: template.shape ? [{ type: template.shape }] : undefined,
    projectile: template.projectile,
    friendlyFire: false,
    cooldownMs: template.cooldownMs ?? 500,
    timings: template.timings,
    icon: template?.icon ?? undefined,
    animations: template.animations ?? {
      cast: 'casting',
      action: 'attack',
      lag: 'exhaust',
    },
    preview: template.preview ?? {
      color: 0xffffff,
      alpha: 0.3,
      type: 'attack-preview',
    },
    vfx: template.vfx,
    effects: [
      ...(template.hitscan
        ? [
            {
              handler: 'hitscan',
              timing: { phase: 'action' as const, percent: 0 },
              params: { range: template.hitscan.range },
            },
          ]
        : []),
      ...(template.effects ?? []).map((e) => ({
        ...e,
        timing: { phase: impactPhase, percent: e.timing?.percent ?? 100 },
      })),
    ],
  };
}

export const TRINKET_MAJOR_REGISTRY: Record<TrinketId, TrinketMajorDefinition> =
  {
    // --- New major trinkets (sentimental objects) ---
    walkman: {
      id: 'walkman',
      icon: 'walkman',
      name: 'Le Baladeur',
      basicAttack: {
        shape: EffectShape.SINGLE,
        range: 1,
        timings: {
          castMs: 60,
          castBlocking: LagType.FULL,
          actionMs: 150,
          actionBlocking: LagType.FULL,
          lagMs: 80,
          lagBlocking: LagType.FULL,
        },
        animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
        preview: { color: 0xffcf3a, alpha: 0.35, type: 'attack-preview' },
        vfx: { sheet: 25 },
        icon: 'spell-icon-basic-walkman',
        cooldownMs: 400,
        effects: [
          {
            handler: 'dealDamage',
            timing: { phase: 'action', percent: 100 },
            params: { amount: 1, type: 'magic', element: 'joy' },
          },
          {
            handler: 'addBuffStack',
            timing: { phase: 'action', percent: 100 },
            params: { buffId: 'walkman-tempo' },
          },
        ],
      },
      maxLevel: 10,
      effectsByLevel: {
        1: {
          modifiers: [{ stat: 'moveSpeed', amount: 5 }],
        },
        2: {
          modifiers: [{ stat: 'moveSpeed', amount: 5 }],
        },
        3: {
          onUnlock: [
            { handler: 'unlockMinor', params: { minorId: 'earphones' } },
          ],
        },
        4: {
          modifiers: [{ stat: 'magicAttack', amount: 2 }],
        },
        5: {
          global: [
            {
              handler: 'addRunBuffMaxStacks',
              params: { buffId: 'walkman-tempo', amount: 1 },
            },
          ],
        },
        6: {
          modifiers: [{ stat: 'magicAttack', amount: 2 }],
        },
        7: {
          onUnlock: [
            { handler: 'unlockMinor', params: { minorId: 'mixtape' } },
          ],
        },
        8: {
          modifiers: [{ stat: 'moveSpeed', amount: 5 }],
        },
        9: {
          modifiers: [{ stat: 'magicAttack', amount: 2 }],
        },
        10: {
          modifiers: [{ stat: 'magicAttack', amount: 3 }],
        },
      },
      sellValue: 0,
    },
    camera: {
      id: 'camera',
      icon: 'camera',
      name: "L'Appareil jetable",
      basicAttack: {
        // Instant photo: scans the facing line (range 4) and hits the first
        // entity. No cast, short action.
        range: 1,
        hitscan: { range: 4 },
        icon: 'spell-icon-basic-camera',
        timings: {
          castMs: 0,
          castBlocking: LagType.FULL,
          actionMs: 100,
          actionBlocking: LagType.FULL,
          lagMs: 150,
          lagBlocking: LagType.FULL,
        },
        animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
        preview: { color: 0xff7ad9, alpha: 0.35, type: 'attack-preview' },
        vfx: { sheet: 15 },
        cooldownMs: 700,
        effects: [
          {
            handler: 'dealDamage',
            timing: { phase: 'action', percent: 100 },
            params: { amount: 1, type: 'magic', element: 'surprise' },
          },
          {
            handler: 'applyBuffInCells',
            timing: { phase: 'action', percent: 100 },
            params: { buffId: 'camera-mark' },
          },
        ],
      },
      maxLevel: 10,
      effectsByLevel: {
        1: {
          modifiers: [{ stat: 'viewRange', amount: 2 }],
        },
        2: {
          modifiers: [{ stat: 'magicAttack', amount: 2 }],
        },
        3: {
          onUnlock: [
            { handler: 'unlockMinor', params: { minorId: 'flashbulb' } },
          ],
        },
        4: {
          modifiers: [{ stat: 'magicAttack', amount: 2 }],
        },
        5: {
          global: [
            {
              handler: 'grantPassive',
              params: {
                id: 'camera-revelateur',
                name: 'Révélateur',
                damageMultiplierConditions: [
                  {
                    multiplier: 1.2,
                    conditions: [
                      {
                        check: 'hasStatus',
                        params: { buffId: 'camera-mark' },
                      },
                    ],
                  },
                ],
              },
            },
          ],
        },
        6: {
          modifiers: [{ stat: 'magicAttack', amount: 2 }],
        },
        7: {
          onUnlock: [
            { handler: 'unlockMinor', params: { minorId: 'film-roll' } },
          ],
        },
        8: {
          global: [
            {
              handler: 'grantPassive',
              params: {
                id: 'camera-revelateur-2',
                name: 'Révélateur II',
                damageMultiplierConditions: [
                  {
                    multiplier: 1.15,
                    conditions: [
                      {
                        check: 'hasStatus',
                        params: { buffId: 'camera-mark' },
                      },
                    ],
                  },
                ],
              },
            },
          ],
        },
        9: {
          modifiers: [{ stat: 'magicAttack', amount: 2 }],
        },
        10: {
          modifiers: [{ stat: 'magicAttack', amount: 3 }],
        },
      },
      sellValue: 0,
    },
    pocketknife: {
      id: 'pocketknife',
      icon: 'pocketknife',
      name: 'Le Canif',
      basicAttack: {
        shape: EffectShape.LINE,
        range: 2,
        directionType: ActionDirectionType.ALIGNED,
        icon: 'spell-icon-basic-pocketknife',
        timings: {
          castMs: 80,
          castBlocking: LagType.FULL,
          actionMs: 200,
          actionBlocking: LagType.FULL,
          lagMs: 120,
          lagBlocking: LagType.FULL,
        },
        animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
        preview: { color: 0xcfd2d6, alpha: 0.35, type: 'attack-preview' },
        vfx: { sheet: 222 },
        cooldownMs: 500,
        effects: [
          {
            handler: 'dealDamageInCells',
            timing: { phase: 'action', percent: 100 },
            params: {
              amount: 2,
              type: 'physical',
              element: 'anger',
              falloffPerCell: 0.75,
            },
          },
          {
            handler: 'addBuffStackInCells',
            timing: { phase: 'action', percent: 100 },
            params: { buffId: 'pocketknife-bleed' },
          },
        ],
      },
      maxLevel: 10,
      effectsByLevel: {
        1: {
          modifiers: [{ stat: 'physicalAttack', amount: 2 }],
        },
        2: {
          global: [
            {
              handler: 'addRunBuffDot',
              params: { buffId: 'pocketknife-bleed', amount: 1 },
            },
          ],
        },
        3: {
          onUnlock: [{ handler: 'unlockMinor', params: { minorId: 'sheath' } }],
        },
        4: {
          global: [
            {
              handler: 'addRunBuffMaxStacks',
              params: { buffId: 'pocketknife-bleed', amount: 1 },
            },
          ],
        },
        5: {
          global: [
            {
              handler: 'addRunBuffDot',
              params: { buffId: 'pocketknife-bleed', amount: 1 },
            },
          ],
        },
        6: {
          modifiers: [{ stat: 'physicalAttack', amount: 2 }],
        },
        7: {
          global: [
            {
              handler: 'grantPassive',
              params: {
                id: 'pocketknife-acharnement',
                name: 'Acharnement',
                damageMultiplierConditions: [
                  {
                    multiplier: 1.2,
                    conditions: [
                      {
                        check: 'statusElapsedAtLeast',
                        params: { buffId: 'pocketknife-bleed', ms: 2000 },
                      },
                    ],
                  },
                ],
              },
            },
          ],
        },
        8: {
          global: [
            {
              handler: 'addRunBuffDot',
              params: { buffId: 'pocketknife-bleed', amount: 1 },
            },
          ],
        },
        9: {
          modifiers: [{ stat: 'physicalAttack', amount: 2 }],
        },
        10: {
          modifiers: [{ stat: 'physicalAttack', amount: 3 }],
        },
      },
      sellValue: 0,
    },
    'baseball-glove': {
      id: 'baseball-glove',
      icon: 'baseball-glove',
      name: 'Le Gant de baseball',
      basicAttack: {
        shape: EffectShape.SWIPE,
        range: 1,
        directionType: ActionDirectionType.LIMITED_ALIGNED,
        icon: 'spell-icon-basic-baseball-glove',
        timings: {
          castMs: 120,
          castBlocking: LagType.FULL,
          actionMs: 250,
          actionBlocking: LagType.FULL,
          lagMs: 150,
          lagBlocking: LagType.FULL,
        },
        animations: { cast: 'casting', action: 'attack', lag: 'exhaust' },
        preview: { color: 0x9b6dff, alpha: 0.35, type: 'attack-preview' },
        vfx: { sheet: 293 },
        cooldownMs: 600,
        effects: [
          {
            handler: 'dealDamageInCells',
            timing: { phase: 'action', percent: 100 },
            params: { amount: 1, type: 'magic', element: 'fear' },
          },
          {
            handler: 'healShield',
            timing: { phase: 'action', percent: 100 },
            params: { amount: 2, requiresHit: true },
          },
        ],
      },
      maxLevel: 10,
      effectsByLevel: {
        1: {
          modifiers: [{ stat: 'maxShield', amount: 8 }],
        },
        2: {
          modifiers: [{ stat: 'maxShield', amount: 4 }],
        },
        3: {
          onUnlock: [
            { handler: 'unlockMinor', params: { minorId: 'baseball-bat' } },
          ],
        },
        4: {
          modifiers: [
            { stat: 'physicalDefense', amount: 1 },
            { stat: 'magicDefense', amount: 1 },
          ],
        },
        5: {
          modifiers: [
            { stat: 'physicalDefense', amount: 1 },
            { stat: 'magicDefense', amount: 1 },
          ],
        },
        6: {
          modifiers: [{ stat: 'maxShield', amount: 6 }],
        },
        7: {
          onUnlock: [
            { handler: 'unlockMinor', params: { minorId: 'baseball-mask' } },
          ],
        },
        8: {
          modifiers: [{ stat: 'shieldRegen', amount: 1 }],
        },
        9: {
          modifiers: [
            { stat: 'physicalDefense', amount: 1 },
            { stat: 'magicDefense', amount: 1 },
          ],
        },
        10: {
          modifiers: [{ stat: 'maxShield', amount: 10 }],
        },
      },
      sellValue: 0,
    },
  };

export const TRINKET_MINOR_REGISTRY: Record<TrinketId, TrinketMinorDefinition> =
  {
    // ---------------------------------------------------------------------------
    // Minors unlocked at Walkman / Pocketknife / Camera level-ups (freely equippable)
    // ---------------------------------------------------------------------------

    earphones: {
      id: 'earphones',
      icon: 'earphones',
      name: 'Écouteurs',
      maxLevel: 1,
      effectsByLevel: {
        1: {
          onAttachedSpell: [
            {
              handler: 'setSpellBypassCooldownAtMaxStacks',
              params: { buffId: 'walkman-tempo' },
            },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 1,
    },

    mixtape: {
      id: 'mixtape',
      icon: 'mixtape',
      name: 'La Cassette',
      maxLevel: 5,
      effectsByLevel: {
        1: {
          onAttachedSpell: [
            {
              handler: 'scaleDamageByCasterBuffStacks',
              params: {
                buffId: 'walkman-tempo',
                perStackPercent: 8,
                perStackPercentPerLevel: 2,
              },
            },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 1,
    },

    sheath: {
      id: 'sheath',
      icon: 'sheath',
      name: "L'Étui",
      maxLevel: 5,
      effectsByLevel: {
        1: {
          global: [
            {
              handler: 'applyConditionalDamageBuff',
              params: {
                id: 'sheath-conditional',
                name: "L'Étui",
                multiplier: 1.15,
                multiplierPerLevel: 0.05,
                check: 'hasStatus',
                conditionBuffId: 'pocketknife-bleed',
              },
            },
          ],
          onAttachedSpell: [
            {
              handler: 'addSpellConditionalDamage',
              params: {
                multiplier: 1.15,
                multiplierPerLevel: 0.05,
                check: 'hasStatus',
                buffId: 'pocketknife-bleed',
              },
            },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 1,
    },

    flashbulb: {
      id: 'flashbulb',
      icon: 'flashbulb',
      name: 'Flash',
      maxLevel: 5,
      effectsByLevel: {
        1: {
          global: [
            {
              handler: 'applyConditionalDamageBuff',
              params: {
                id: 'flashbulb-conditional',
                name: 'Flash',
                multiplier: 1.2,
                multiplierPerLevel: 0.05,
                check: 'hasStatus',
                conditionBuffId: 'camera-mark',
              },
            },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 1,
    },

    'film-roll': {
      id: 'film-roll',
      icon: 'film-roll',
      name: 'Pellicule',
      maxLevel: 1,
      effectsByLevel: {
        1: {
          global: [
            {
              handler: 'grantPassive',
              params: {
                id: 'film-roll-buff',
                name: 'Pellicule',
                hooks: [
                  {
                    event: 'onKill',
                    handler: 'applyBuffInNeighborsCells',
                    params: { buffId: 'camera-mark', radius: 1 },
                  },
                ],
              },
            },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 1,
    },

    'baseball-bat': {
      id: 'baseball-bat',
      icon: 'baseball-bat',
      name: 'La Batte',
      maxLevel: 1,
      effectsByLevel: {
        1: {
          global: [
            {
              handler: 'grantPassive',
              params: {
                id: 'baseball-bat-buff',
                name: 'La Batte',
                hooks: [
                  {
                    event: 'onDamageTaken',
                    handler: 'applyBuffInNeighborsCells',
                    params: { buffId: 'baseball-bat-slow', radius: 1 },
                  },
                ],
              },
            },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 1,
    },

    'baseball-mask': {
      id: 'baseball-mask',
      icon: 'baseball-mask',
      name: 'Le Masque de baseball',
      maxLevel: 5,
      effectsByLevel: {
        1: {
          global: [
            {
              handler: 'applyShieldOnLowHpBuff',
              params: {
                id: 'baseball-mask-buff',
                name: 'Le Masque de baseball',
                amount: 10,
                amountPerLevel: 5,
                hpThreshold: 30,
              },
            },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 1,
    },

    // ---------------------------------------------------------------------------
    // Elemental & universal minor trinkets
    // ---------------------------------------------------------------------------

    // --- Elemental charms ---

    'bright-charm': {
      id: 'bright-charm',
      name: 'La Veilleuse',
      icon: 'bright-charm',
      maxLevel: 5,
      statScaling: {
        modifiers: [{ stat: 'maxShield', amountPerLevel: 10 }],
        lifeStealPercentPerLevel: 0.01,
      },
      sellValue: 0,
      socketSlots: 2,
    },

    'gloom-charm': {
      id: 'gloom-charm',
      name: 'Le Parapluie',
      icon: 'gloom-charm',
      maxLevel: 5,
      statScaling: { modifiers: [{ stat: 'magicDefense', amountPerLevel: 4 }] },
      effectsByLevel: {
        1: {
          onAttachedSpell: [
            {
              handler: 'addSpellEffectStep',
              params: {
                step: {
                  handler: 'applyBuffInCells',
                  params: { buffId: 'chill', targetTeam: 'opposite' },
                },
              },
            },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 2,
    },

    'fury-charm': {
      id: 'fury-charm',
      name: 'Le Briquet',
      icon: 'fury-charm',
      maxLevel: 5,
      statScaling: {
        modifiers: [{ stat: 'physicalAttack', amountPerLevel: 4 }],
      },
      effectsByLevel: {
        1: {
          onAttachedSpell: [
            { handler: 'addSpellDamagePercent', params: { percent: 15 } },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 2,
    },

    'dread-charm': {
      id: 'dread-charm',
      name: 'Le Miroir fêlé',
      icon: 'dread-charm',
      maxLevel: 5,
      statScaling: {
        modifiers: [{ stat: 'critMultiplier', amountPerLevel: 0.2 }],
      },
      effectsByLevel: {
        1: {
          onAttachedSpell: [
            {
              handler: 'addSpellEffectStep',
              params: {
                step: {
                  handler: 'applyBuffWithChance',
                  params: { chance: 25, buffId: 'stunned', durationMs: 600 },
                },
              },
            },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 2,
    },

    'flash-charm': {
      id: 'flash-charm',
      name: 'La Toupie',
      icon: 'flash-charm',
      maxLevel: 5,
      statScaling: { modifiers: [{ stat: 'moveSpeed', amountPerLevel: 10 }] },
      effectsByLevel: {
        1: {
          onAttachedSpell: [
            { handler: 'addSpellRange', params: { amount: 1 } },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 2,
    },

    'rot-charm': {
      id: 'rot-charm',
      name: "L'Éponge",
      icon: 'rot-charm',
      maxLevel: 5,
      statScaling: { modifiers: [{ stat: 'magicAttack', amountPerLevel: 4 }] },
      effectsByLevel: {
        1: {
          onAttachedSpell: [
            {
              handler: 'addSpellEffectStep',
              params: {
                step: {
                  handler: 'applyBuffInCells',
                  params: { buffId: 'poison', targetTeam: 'opposite' },
                },
              },
            },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 2,
    },

    // --- Utility universals ---

    'vigor-band': {
      id: 'vigor-band',
      name: 'Le Bandeau',
      icon: 'vigor-band',
      maxLevel: 5,
      statScaling: { modifiers: [{ stat: 'maxLife', amountPerLevel: 20 }] },
      sellValue: 0,
      socketSlots: 2,
    },

    'keen-edge': {
      id: 'keen-edge',
      name: 'Le Coupe-papier',
      icon: 'keen-edge',
      maxLevel: 5,
      statScaling: {
        modifiers: [
          { stat: 'critChancePhysical', amountPerLevel: 5 },
          { stat: 'critChanceMagic', amountPerLevel: 5 },
        ],
      },
      sellValue: 0,
      socketSlots: 2,
    },

    'swift-cast': {
      id: 'swift-cast',
      name: 'Le Réveil',
      icon: 'swift-cast',
      maxLevel: 5,
      statScaling: {
        modifiers: [{ stat: 'spellCastTime', percentPerLevel: -6 }],
      },
      effectsByLevel: {
        1: {
          onAttachedSpell: [
            { handler: 'addSpellCooldownPercent', params: { percent: -10 } },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 2,
    },

    'long-reach': {
      id: 'long-reach',
      name: 'Les Jumelles',
      icon: 'long-reach',
      maxLevel: 5,
      statScaling: { modifiers: [{ stat: 'viewRange', amountPerLevel: 1 }] },
      effectsByLevel: {
        1: {
          onAttachedSpell: [
            { handler: 'addSpellRange', params: { amount: 1 } },
          ],
        },
      },
      sellValue: 0,
      socketSlots: 2,
    },

    // --- Blank charm (pure socket carrier) ---

    'blank-charm': {
      id: 'blank-charm',
      name: 'Le Porte-clés',
      icon: 'blank-charm',
      maxLevel: 1,
      sellValue: 0,
      socketSlots: 3,
    },
  };
